import { Box, Flex } from '@chakra-ui/react';

export interface SegmentOption<T extends string | number> {
  value: T;
  label: string;
}

export interface SegmentedControlProps<T extends string | number> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentOption<T>[];
}

/**
 * Figma-styled segmented control (the "Editable · Vector · Image" tab row).
 * A dark track with a raised pill on the selected segment.
 */
export function SegmentedControl<T extends string | number>({
  value,
  onChange,
  options,
}: SegmentedControlProps<T>) {
  return (
    /*
       pdf-to-figma's track: no border, `p="0.5"`, `gap="0.5"`. The border and the roomier
       padding were ours, and together they made this control noticeably taller than the one
       the same question uses in pdf.
    */
    <Flex bg="figma.bgDark" borderRadius="md" p="0.5" gap="0.5">
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <Box
            as="button"
            key={String(option.value)}
            flex="1"
            onClick={() => onChange(option.value)}
            py="1"
            borderRadius="sm"
            fontSize="2xs"
            fontWeight="500"
            textAlign="center"
            bg={selected ? 'figma.panel' : 'transparent'}
            color={selected ? 'figma.text' : 'figma.textMuted'}
            boxShadow={selected ? 'sm' : 'none'}
            transition="all 0.15s"
            {...(selected ? {} : { _hover: { color: 'figma.text' } })}
          >
            {option.label}
          </Box>
        );
      })}
    </Flex>
  );
}
